import React, { useContext } from 'react'
import { getBadgeTheme, getProductTag } from '@helpers/products'
import PriceComparison from '@components/ui/PriceComparison'
import Badge, { BadgeType } from '@components/ui/Badge'
import ProductType from '@components/ui/ProductType'
import YotpoStarRating, {
  YotpoStarRatingProps
} from '@components/product/yotpoRatings/YotpoStarRatings'
import { BREAKPOINT_SIZES, MTO_TAG, PRODUCT_TYPE_TAGS } from '@consts/index'
import classNames from 'classnames'
import { ProductWithVariants } from 'types/product'
import ProductContext from '../context'
import productHeader from './productHeader.module.scss'
import { useMediaQuery } from '@helpers/hooks'

const BADGE_TYPE_RULES: {
  exact: Record<string, 'outline'>
  patterns: (string | RegExp)[]
} = {
  exact: {
    organic: 'outline',
    [MTO_TAG.toLowerCase()]: 'outline'
  },
  patterns: ['priority', /beta/i, /pro/i]
}

const resolveBadgeType = (raw: string, formatted: string): BadgeType => {
  const rawLower = raw.toLowerCase()

  const exactType =
    BADGE_TYPE_RULES.exact[rawLower as keyof typeof BADGE_TYPE_RULES.exact]

  if (exactType) return exactType

  const patternMatch = BADGE_TYPE_RULES.patterns.some(pattern =>
    typeof pattern === 'string'
      ? rawLower.includes(pattern)
      : pattern.test(formatted)
  )

  if (patternMatch) return 'outline'

  return 'filled'
}

type ProductHeaderProps = {
  product: ProductWithVariants
  byline: string
  classes?: string
} & { starRatings?: YotpoStarRatingProps['starRatings'] }

const ProductHeader = ({
  product,
  byline = '',
  starRatings,
  classes = undefined
}: ProductHeaderProps) => {
  const { discount } = useContext(ProductContext)
  const isMobileDown = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.TABLET - 1
  })

  const showStarRating = !!starRatings && starRatings.total_review > 0
  const productTags = product?.tags?.split(',') ?? []
  const tagInfo = getProductTag(productTags, PRODUCT_TYPE_TAGS)

  const productHeaderContainer = classNames(
    productHeader.productHeader__container,
    classes
  )

  const badgeProps = ({
    raw,
    formatted
  }: {
    raw: string
    formatted: string
  }) => {
    const badgeType = resolveBadgeType(raw, formatted)

    return {
      title: formatted,
      themeColour: getBadgeTheme({ raw, formatted }),
      badgeType: badgeType,
      ...(isMobileDown ? { smallX2: true } : { medium: true })
    }
  }

  return (
    <div className={productHeaderContainer}>
      <div className={productHeader.productHeader__tagTop}>
        {tagInfo && <Badge {...badgeProps(tagInfo)} small />}
        {discount.title && (
          <Badge
            badgeType="filled"
            themeColour={getBadgeTheme({
              raw: 'discount',
              formatted: 'discount'
            })}
            title={discount.title}
            small
          />
        )}
      </div>
      <header className={productHeader.productHeader__headlineContainer}>
        <h1 className={productHeader.productHeader__title}>
          <ProductType text={product.product_type} />
        </h1>{' '}
      </header>
      <h3 className={productHeader.productHeader__byline}>{byline}</h3>
      <div className={productHeader.productHeader__row}>
        {showStarRating && <YotpoStarRating starRatings={starRatings} />}
        {!!product.price && (
          <div className={productHeader.ProductHeader__prices}>
            <PriceComparison
              price={product.price}
              fullPrice={product.compare_at_price}
              size="medium-bold"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductHeader
